<script setup>
definePage({
  meta: {
    action: 'read',
    subject: 'Med',
    navActiveLink: 'operational-infractions',
  },
})

import Breadcrumbs from '@/components/global/Breadcrumbs.vue'
import PageHeader from '@/components/global/PageHeader.vue'
import ActiveRulesTab from '@/components/operational/infractions/automation/ActiveRulesTab.vue'
import CreateAutomationRuleDialog from '@/components/operational/infractions/automation/CreateAutomationRuleDialog.vue'
import ExecutionLogsTab from '@/components/operational/infractions/automation/ExecutionLogsTab.vue'
import {
  extractAutomationExecutionsListPayload,
  isAutomationExecutionsListResponseOk,
  mapApiAutomationExecutionToRow,
} from '@/components/operational/infractions/automation/automationExecutionsMap'
import {
  extractAutomationRulesListPayload,
  isAutomationRulesListResponseOk,
  mapApiAutomationRuleToRow,
} from '@/components/operational/infractions/automation/automationRulesMap'
import { fetchMedAutomationExecutionsList, fetchMedAutomationRulesList } from '@/api'
import InfractionsPaginationFooter from '@/components/operational/infractions/ui/InfractionsPaginationFooter.vue'
import InfractionsTabs from '@/components/operational/infractions/ui/InfractionsTabs.vue'
import { PAGINATION_OPTIONS } from '@/utils/constants'

const router = useRouter()
const vm = getCurrentInstance()
const toast = useToast()

const tab = ref('active-rules')
const rulesTableLoading = ref(false)
const executionsTableLoading = ref(false)
const ruleItems = ref([])
const executionItems = ref([])
const rulesPageNum = ref(PAGINATION_OPTIONS.PAGE_NUM)
const rulesPageSize = ref(PAGINATION_OPTIONS.PAGE_SIZE)
const rulesTotal = ref(0)
const executionsPageNum = ref(PAGINATION_OPTIONS.PAGE_NUM)
const executionsPageSize = ref(PAGINATION_OPTIONS.PAGE_SIZE)
const executionsTotal = ref(0)
const isCreateRuleDialogVisible = ref(false)
const rulesLoadFailed = ref(false)
const executionsLoadFailed = ref(false)

const automationTabOptions = [
  { value: 'active-rules', labelKey: 'infractionsAutomationModule.tabs.activeRules' },
  { value: 'execution-logs', labelKey: 'infractionsAutomationModule.tabs.executionLogs' },
]

const activeTabTotal = computed(() => (tab.value === 'active-rules' ? rulesTotal.value : executionsTotal.value))

const rulesPaginationLabel = computed(() => {
  if (!rulesTotal.value) {
    return vm.proxy.$t('infractionsAutomationModule.pagination.emptyRules')
  }

  const start = ((rulesPageNum.value - 1) * rulesPageSize.value) + 1
  const end = Math.min(rulesPageNum.value * rulesPageSize.value, rulesTotal.value)

  return vm.proxy.$t('infractionsAutomationModule.pagination.summaryRules', {
    start,
    end,
    total: rulesTotal.value,
  })
})

const executionsPaginationLabel = computed(() => {
  if (!executionsTotal.value) {
    return vm.proxy.$t('infractionsAutomationModule.pagination.emptyExecutions')
  }

  const start = ((executionsPageNum.value - 1) * executionsPageSize.value) + 1
  const end = Math.min(executionsPageNum.value * executionsPageSize.value, executionsTotal.value)

  return vm.proxy.$t('infractionsAutomationModule.pagination.summaryExecutions', {
    start,
    end,
    total: executionsTotal.value,
  })
})

const fetchRulesList = async () => {
  rulesTableLoading.value = true
  try {
    const res = await fetchMedAutomationRulesList({
      page: rulesPageNum.value,
      page_size: rulesPageSize.value,
    })

    if (!isAutomationRulesListResponseOk(res)) {
      toast.error(res?.message || vm.proxy.$t('infractionsAutomationModule.fetchFailed'))
      ruleItems.value = []
      rulesTotal.value = 0
      rulesLoadFailed.value = true
      return
    }

    const { items, total } = extractAutomationRulesListPayload(res)
    ruleItems.value = (items || []).map(mapApiAutomationRuleToRow)
    rulesTotal.value = total
    rulesLoadFailed.value = false
  } catch (error) {
    console.error('fetchMedAutomationRulesList failed', error)
    toast.error(vm.proxy.$t('infractionsAutomationModule.fetchError'))
    ruleItems.value = []
    rulesTotal.value = 0
    rulesLoadFailed.value = true
  } finally {
    rulesTableLoading.value = false
  }
}

const fetchExecutionsList = async () => {
  executionsTableLoading.value = true
  try {
    const res = await fetchMedAutomationExecutionsList({
      page: executionsPageNum.value,
      page_size: executionsPageSize.value,
    })

    if (!isAutomationExecutionsListResponseOk(res)) {
      toast.error(res?.message || vm.proxy.$t('infractionsAutomationModule.executionLogsFetchFailed'))
      executionItems.value = []
      executionsTotal.value = 0
      executionsLoadFailed.value = true
      return
    }

    const { items, total } = extractAutomationExecutionsListPayload(res)
    executionItems.value = (items || []).map(mapApiAutomationExecutionToRow)
    executionsTotal.value = total
    executionsLoadFailed.value = false
  } catch (error) {
    console.error('fetchMedAutomationExecutionsList failed', error)
    toast.error(vm.proxy.$t('infractionsAutomationModule.executionLogsFetchError'))
    executionItems.value = []
    executionsTotal.value = 0
    executionsLoadFailed.value = true
  } finally {
    executionsTableLoading.value = false
  }
}

const loadActiveTab = () => {
  if (tab.value === 'active-rules') {
    fetchRulesList()
    return
  }

  if (tab.value === 'execution-logs') {
    fetchExecutionsList()
  }
}

const goBackToInfractions = () => {
  router.push('/operational/infractions/1')
}

const handleCreateRule = () => {
  isCreateRuleDialogVisible.value = true
}

const handleCreateRuleSuccess = () => {
  rulesPageNum.value = PAGINATION_OPTIONS.PAGE_NUM
  if (tab.value === 'active-rules') {
    fetchRulesList()
  }
}

const onRulesPageChange = value => {
  rulesPageNum.value = value
  fetchRulesList()
}

const onRulesPageSizeChange = value => {
  rulesPageSize.value = Number(value)
  rulesPageNum.value = PAGINATION_OPTIONS.PAGE_NUM
  fetchRulesList()
}

const onExecutionsPageChange = value => {
  executionsPageNum.value = value
  fetchExecutionsList()
}

const onExecutionsPageSizeChange = value => {
  executionsPageSize.value = Number(value)
  executionsPageNum.value = PAGINATION_OPTIONS.PAGE_NUM
  fetchExecutionsList()
}

const handleRuleDeleted = () => {
  if (ruleItems.value.length === 1 && rulesPageNum.value > 1) {
    rulesPageNum.value -= 1
  }
  fetchRulesList()
}

watch(tab, () => {
  loadActiveTab()
})

onMounted(() => {
  loadActiveTab()
})
</script>

<template>
  <div class="infractions-automation-page">
    <Breadcrumbs
      class="mb-2"
      :items="[
        { title: $t('breadCrumbs.infractions'), to: '/operational/infractions/1' },
        { title: $t('breadCrumbs.infractionsAutomation'), disabled: true },
      ]"
    />
    <div class="automation-page-heading d-flex align-center justify-space-between flex-wrap gap-4">
      <PageHeader
        :title="$t('pageHeaders.infractionsAutomation.title')"
        :subtitle="$t('pageHeaders.infractionsAutomation.subtitle')"
      />
      <div class="automation-page-card__actions d-flex align-center flex-wrap gap-2">
        <VBtn
          variant="outlined"
          color="secondary"
          prepend-icon="tabler-arrow-left"
          @click="goBackToInfractions"
        >
          {{ $t('infractionsAutomationModule.backToInfractions') }}
        </VBtn>
        <VBtn
          color="primary"
          prepend-icon="tabler-plus"
          @click="handleCreateRule"
        >
          {{ $t('infractionsAutomationModule.createRule') }}
        </VBtn>
      </div>
    </div>

    <VCard
      class="automation-page-card mb-4"
      variant="flat"
      border
    >
      <InfractionsTabs
        v-model="tab"
        :options="automationTabOptions"
        :active-count="activeTabTotal"
      />
    </VCard>

    <VWindow
      v-model="tab"
      :transition="false"
    >
      <VWindowItem
        value="active-rules"
        eager
      >
        <ActiveRulesTab
          :items="ruleItems"
          :loading="rulesTableLoading"
          :load-failed="rulesLoadFailed"
          @status-changed="fetchRulesList"
          @rule-deleted="handleRuleDeleted"
          @retry="fetchRulesList"
        />
        <InfractionsPaginationFooter
          :page="rulesPageNum"
          :page-size="rulesPageSize"
          :total="rulesTotal"
          :summary="rulesPaginationLabel"
          @update:page="onRulesPageChange"
          @update:page-size="onRulesPageSizeChange"
        />
      </VWindowItem>

      <VWindowItem
        value="execution-logs"
        eager
      >
        <ExecutionLogsTab
          :items="executionItems"
          :loading="executionsTableLoading"
          :load-failed="executionsLoadFailed"
          @retry="fetchExecutionsList"
        />
        <InfractionsPaginationFooter
          :page="executionsPageNum"
          :page-size="executionsPageSize"
          :total="executionsTotal"
          :summary="executionsPaginationLabel"
          @update:page="onExecutionsPageChange"
          @update:page-size="onExecutionsPageSizeChange"
        />
      </VWindowItem>
    </VWindow>

    <CreateAutomationRuleDialog
      v-model:is-dialog-visible="isCreateRuleDialogVisible"
      @success="handleCreateRuleSuccess"
    />
  </div>
</template>

<style lang="scss" scoped>
.automation-page-card {
  padding: 20px;
}

.automation-page-heading > .page-header {
  min-inline-size: 0;
  flex: 1 1 320px;
}

@media (max-width: 599.98px) {
  .automation-page-card__actions {
    inline-size: 100%;

    > .v-btn {
      flex: 1 1 100%;
    }
  }
}
</style>
